import moment from 'moment';
import 'moment/locale/pt-br';

export const normalizeCNPJ = (cnpj) => {
  if (!cnpj) return '';

  const onlyNumbers = cnpj.replace(/\D/g, '');

  return onlyNumbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

export const formatMoney = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value));
};

export const validateCPF = (value) => {
  const val = value.replace(/[^\d]+/g, '').split('');

  const allDigitsIsEquals = val.every((digit) => digit === val[0]);
  if (allDigitsIsEquals) return false;

  const calculateRestOfDivision = (validatorDigitIndex) => {
    const sum = val
      .slice(0, validatorDigitIndex)
      .reduce(
        (acc, current, index) => acc + parseInt(current, 10) * (validatorDigitIndex + 1 - index),
        0
      );
    let restOfDivision = (sum * 10) % 11;
    if (restOfDivision === 10 || restOfDivision === 11) restOfDivision = 0;
    return restOfDivision;
  };

  const isValidFirstDigit = calculateRestOfDivision(9) === parseInt(val[9], 10);
  const isValidSecondDigit = calculateRestOfDivision(10) === parseInt(val[10], 10);

  return isValidFirstDigit && isValidSecondDigit;
};

export function validateCNPJ(cnpj) {
  cnpj = cnpj.replace(/[^\d]/g, '');

  if (cnpj.length !== 14) {
    return false;
  }

  let sum = 0;
  let weight = 2;
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (parseInt(cnpj.charAt(12)) !== digit) {
    return false;
  }

  sum = 0;
  weight = 2;
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (parseInt(cnpj.charAt(13)) !== digit) {
    return false;
  }

  return true;
}

export const formatCpfCnpj = (value) => {
  const onlyDigits = (value || '').replace(/\D/g, '');
  if (onlyDigits.length > 11) {
    return onlyDigits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5');
  } else {
    return onlyDigits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4');
  }
};

export const formatPhone = (value) => {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.replace(/^(\d{2})(\d{1})?(\d{4})(\d{4})$/, '($1) $2$3-$4');
};

export const formatCep = (value) => {
  if (!value) return '';

  const cleaned = value.replace(/\D/g, '');
  return cleaned.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

// Formatar valor monetário para exibição
export const formatCurrency = (value) => {
  if (typeof value === 'number') {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const str = String(value);

  const isUSFormat = /^\d+\.\d+$/.test(str);
  const numValue = isUSFormat
    ? parseFloat(str)
    : parseFloat(str.replace(/\./g, '').replace(',', '.'));

  if (isNaN(numValue)) return '0,00';
  return numValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Converter string monetária para número
export const parseMonetaryValue = (value) => {
  if (!value) return 0;
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  return parseFloat(normalized) || 0;
};

export const parseCurrencyInput = (rawValue) => {
  const onlyDigits = String(rawValue).replace(/\D/g, '');
  if (!onlyDigits) return { display: '', numeric: '' };
  const numeric = parseInt(onlyDigits, 10) / 100;
  return { display: formatCurrency(numeric), numeric };
};

// Converter string de porcentagem para número
export const parsePercentValue = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(',', '.');
  return parseFloat(cleaned) || 0;
};

export const formatDateBR = (dateString, showTime = true) => {
  if (!dateString) return '-';

  const format = showTime ? 'DD/MM/YYYY, HH:mm' : 'DD/MM/YYYY';

  return moment(dateString).format(format);
};

export const formatPercent = (value) => {
  if (!value && value !== 0) return '';
  return String(parseFloat(value)).replace('.', ',');
};

// Formata taxa/percentual com mínimo de 1 casa decimal e máximo de 2, sem zeros desnecessários
export const formatRate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return `${num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
};

export const parseISOToDate = (value) => {
  if (!value) return '';
  return value.split('T')[0];
};

export const isCPF = (document) => document && document.replace(/\D/g, '').length === 11;

export const formatNumericInput = (value, { type = 'currency', mode = 'input' } = {}) => {
  if (value === null || value === undefined || value === '') return '';

  if (mode === 'input') {
    const digits = String(value).replace(/\D/g, '');

    if (!digits) return '0,00';

    const padded = digits.padStart(3, '0');
    const cents = padded.slice(-2);
    let intRaw = padded.slice(0, -2).replace(/^0+/, '') || '0';

    if (type === 'currency') {
      intRaw = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return `${intRaw},${cents}`;
  }

  if (mode === 'display') {
    const fixed = Number(value).toFixed(2);
    const [int, dec] = fixed.split('.');

    let intFormatted = int;

    if (type === 'currency') {
      intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return `${intFormatted},${dec}`;
  }

  return '';
};

export const parseBR = (value) => {
  if (!value) return null;
  return parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

// Reconcilia a soma das parcelas customizadas com o total pago oficial,
// jogando a diferença inteira (juros/IOF/TAC) na última parcela.
export const reconcileInstallmentValues = (customDates, totalPaid) => {
  if (!Array.isArray(customDates) || customDates.length === 0) return [];

  if (!(totalPaid > 0)) {
    return customDates.map((d) => ({ date: d.date, value: 0 }));
  }

  const targetCents = Math.round(totalPaid * 100);
  const cents = customDates.map((d) => Math.round((d.value || 0) * 100));
  const sumCents = cents.reduce((a, b) => a + b, 0);
  const diffCents = targetCents - sumCents;

  if (diffCents !== 0) {
    const lastIdx = cents.length - 1;
    cents[lastIdx] = Math.max(1, cents[lastIdx] + diffCents);
  }

  return customDates.map((d, idx) => ({ date: d.date, value: cents[idx] / 100 }));
};
